import { Body } from "@sprout/typography";
import useTranslation from "@hooks/use_translation";
import { useSafePostHog } from "@hooks/use_posthog";
import GoogleLogo from "@images/google_logo.svg";
import AppleLogo from "@images/apple_logo.svg";
import LinkedInLogo from "@images/linkedin_logo.svg";

import "./social_login_buttons.scss";

const PROVIDER_LOGOS: Record<string, string> = {
  google_oauth2: GoogleLogo,
  apple: AppleLogo,
  linkedin: LinkedInLogo,
};

export type SocialProvider = {
  id: string;
  name: string;
  path: string;
};

export type SocialLogin = {
  authenticityToken: string;
  returnPath: string | null;
  lastUsedProvider: string | null;
  providers: SocialProvider[];
};

type SocialLoginButtonsProps = {
  socialLogin: SocialLogin;
  jobBoardToken?: string;
};

const SocialLoginButtons = ({ socialLogin, jobBoardToken }: SocialLoginButtonsProps) => {
  const { t } = useTranslation("misc");
  const posthog = useSafePostHog();
  const { authenticityToken, returnPath, lastUsedProvider, providers } = socialLogin;

  const timeZone: string | undefined = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const requestPhaseUrl = (provider: SocialProvider) => {
    const params = new URLSearchParams();
    if (jobBoardToken) params.set("job_board_token", jobBoardToken);
    if (returnPath) params.set("return_path", returnPath);
    if (timeZone) params.set("time_zone", timeZone);
    const query = params.toString();

    return query ? `${provider.path}?${query}` : provider.path;
  };

  const renderableProviders = providers.filter((provider) => PROVIDER_LOGOS[provider.id]);

  const trackClick = (provider: SocialProvider) =>
    posthog?.capture(
      "social_login_clicked",
      {
        provider: provider.name,
      },
      { transport: "sendBeacon" },
    );

  if (renderableProviders.length === 0) {
    return null;
  }

  return (
    <div className="social-login" role="group" aria-label={t("login.social.groupLabel")}>
      <Body as="span" secondary className="social-login__label">
        {t("login.social.label")}
      </Body>

      <div className="social-login__buttons">
        {renderableProviders.map((provider) => {
          const isLastUsed = provider.id === lastUsedProvider;
          const continueLabel = t("login.social.continueWith", { provider: provider.name });

          return (
            <form
              key={provider.id}
              className="social-login__form"
              method="post"
              action={requestPhaseUrl(provider)}
              onSubmit={() => trackClick(provider)}
            >
              <input type="hidden" name="authenticity_token" value={authenticityToken} />

              <button
                type="submit"
                className="social-login__button"
                aria-label={
                  isLastUsed ? `${continueLabel} (${t("login.social.lastUsed")})` : continueLabel
                }
              >
                <img src={PROVIDER_LOGOS[provider.id]} alt="" className="social-login__logo" />

                <span className="social-login__name">{provider.name}</span>

                {isLastUsed && (
                  <span className="social-login__badge" aria-hidden="true">
                    {t("login.social.lastUsed")}
                  </span>
                )}
              </button>
            </form>
          );
        })}
      </div>
    </div>
  );
};

export default SocialLoginButtons;
